import { Link, useLocation, useMatch, useResolvedPath } from "react-router-dom";
import { CampingRoute } from "../router/CampingRoute";
import { Burger } from "./Burger";
import { PropsWithChildren, useEffect, useState } from "react";
import ReactDOM from "react-dom";
import type { LinkProps } from "react-router-dom";
import logo from "./logo.png";

interface CustomLinkProps {
  $isMobile?: boolean;
}

function CustomLink({ children, to, ...props }: LinkProps & CustomLinkProps) {
  let resolved = useResolvedPath(to);
  let match = useMatch({ path: resolved.pathname, end: true });
  const isActive = Boolean(match);
  const mobileClasses = isActive
    ? "bg-[#f4e2af] text-[#1f2d18] animate-[menu-active-in_380ms_ease-out]"
    : "text-[#f6f8f0] hover:bg-[#f4e2af]/15";
  const desktopClasses = isActive
    ? "bg-[#f4e2af] text-[#1f2d18] shadow-[0_8px_20px_rgba(6,20,10,0.28)] animate-[menu-active-in_380ms_ease-out]"
    : "text-white hover:bg-white/15";

  return (
    <Link
      to={to}
      {...props}
      className={`no-underline ${
        props.$isMobile
          ? `block w-full rounded-2xl px-4 py-3 text-center text-lg font-semibold transition-colors ${mobileClasses}`
          : `whitespace-nowrap rounded-full px-2.5 py-2 text-xs font-semibold uppercase tracking-[0.08em] transition-colors lg:px-5 lg:py-3 lg:text-sm lg:tracking-[0.12em] ${desktopClasses}`
      }`}
    >
      {children}
    </Link>
  );
}

interface NavContainerProps {
  isOpen: boolean;
}

function NavContainer({ isOpen, children }: PropsWithChildren<NavContainerProps>) {
  if (!isOpen) {
    return null;
  }

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[70] bg-[#102010]/95 backdrop-blur-sm md:hidden">
      {children}
    </div>,
    document.body
  );
}

export interface MenuProps {
  pages: CampingRoute[];
  isHome: boolean;
}

export function Menu({ pages, isHome }: MenuProps) {
  const [isOpen, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  return (
    <div
      data-menu-root
      className={`relative z-20 pt-4 sm:pt-6 ${isHome ? "" : "pb-4 sm:pb-6"}`}
    >
      <div
        className={`flex items-center md:hidden ${
          isHome ? "justify-end" : "w-full justify-between"
        }`}
      >
        {!isHome && (
          <img
            src={logo}
            alt="Camping des Iles 2008"
            className="h-14 w-auto drop-shadow-[0_6px_18px_rgba(0,0,0,0.5)]"
          />
        )}
        <Burger
          isOpen={isOpen}
          size="large"
          onClick={() => setOpen((prev) => !prev)}
        />
      </div>

      <div className="hidden rounded-2xl border border-white/35 bg-white/0 shadow-[0_10px_30px_rgba(4,20,10,0.35)] backdrop-blur-md md:block">
        <div className="flex items-center justify-center px-3 py-3 sm:px-4 lg:px-5">
          <nav className="flex flex-1 items-center justify-center gap-1 lg:gap-2">
            {pages.map((page) => (
              <CustomLink key={page.path} to={page.path}>
                {page.title}
              </CustomLink>
            ))}
          </nav>
        </div>
      </div>

      <NavContainer isOpen={isOpen}>
        <div className="mx-auto flex h-full w-full max-w-sm flex-col px-6 pb-8 pt-6">
          <div
            className={`flex items-center ${
              isHome ? "justify-end" : "justify-between"
            }`}
          >
            {!isHome && (
              <img
                src={logo}
                alt="Camping des Iles 2008"
                className="h-14 w-auto drop-shadow-[0_6px_18px_rgba(0,0,0,0.5)]"
              />
            )}
            <Burger isOpen={isOpen} size="large" onClick={() => setOpen(false)} />
          </div>

          <nav className="mt-8 flex flex-1 flex-col gap-3">
            {pages.map((page) => (
              <CustomLink
                key={page.path}
                to={page.path}
                $isMobile
                onClick={() => setOpen(false)}
              >
                {page.title}
              </CustomLink>
            ))}
          </nav>

          <p className="mt-8 text-center text-sm text-emerald-100/80">
            Camping des Iles 2008
          </p>
        </div>
      </NavContainer>
    </div>
  );
}
